import JSZip from 'jszip';
import { IST } from '../../lib/dayjsIst';
import { attachmentMime, isImage, isPdf } from './healthUtils';

function sanitizeFilename(name) {
  return String(name).replace(/[\\/:*?"<>|]/g, '_');
}

export function isBillType(type) {
  return String(type || '').trim().toLowerCase() === 'bill';
}

function istTimestampForFilename(dateVal) {
  if (!dateVal) return 'unknown';
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  let year = '';
  let month = '';
  let day = '';
  let hour = '';
  let minute = '';
  for (let i = 0; i < parts.length; i += 1) {
    const p = parts[i];
    if (p.type === 'year') year = p.value;
    else if (p.type === 'month') month = p.value;
    else if (p.type === 'day') day = p.value;
    else if (p.type === 'hour') hour = p.value;
    else if (p.type === 'minute') minute = p.value;
  }
  return `${year}${month}${day}_${hour}${minute}`;
}

function sanitizePart(s) {
  return String(s).trim().replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_');
}

export function downloadBaseName(file) {
  if (file.file_name && String(file.file_name).trim()) {
    return sanitizePart(String(file.file_name).trim());
  }
  if (isBillType(file.type) && file.bill_number && String(file.bill_number).trim()) {
    return sanitizePart(file.bill_number);
  }
  const parts = [];
  if (file.type && String(file.type).trim()) parts.push(sanitizePart(file.type));
  if (file.report_type && String(file.report_type).trim()) parts.push(sanitizePart(file.report_type));
  parts.push(istTimestampForFilename(file.date));
  return parts.join('_');
}

function extFromMime(mime) {
  if (isPdf(mime)) return 'pdf';
  if (mime && String(mime).toLowerCase().includes('png')) return 'png';
  return 'jpg';
}

function fileNameBase(file) {
  if (!file.file_name || !String(file.file_name).trim()) return '';
  return sanitizeFilename(sanitizePart(String(file.file_name).trim()));
}

export function downloadFilenameForAttachment(file, attachment, index, total) {
  const mime = attachmentMime(attachment);
  const ext = extFromMime(mime);
  const customBase = fileNameBase(file);

  if (customBase) {
    if (total === 1) return `${customBase}.${ext}`;
    return `${customBase}_${index + 1}.${ext}`;
  }

  const base = downloadBaseName(file);
  if (total === 1) return `${base}.${ext}`;
  return `${base}_${index + 1}.${ext}`;
}

export function attachmentsForDownload(file) {
  const attachments = Array.isArray(file.attachments) ? file.attachments : [];
  let pdf = null;
  const images = [];
  for (let i = 0; i < attachments.length; i += 1) {
    const a = attachments[i];
    if (!a || !a.key) continue;
    const mime = attachmentMime(a);
    if (isPdf(mime)) pdf = a;
    else if (isImage(mime)) images.push(a);
  }
  if (pdf) return [pdf];
  return images;
}

function ensureUnique(filename, used) {
  const safe = sanitizeFilename(filename);
  if (!used.has(safe)) {
    used.add(safe);
    return safe;
  }
  const dot = safe.lastIndexOf('.');
  const base = dot > 0 ? safe.slice(0, dot) : safe;
  const ext = dot > 0 ? safe.slice(dot) : '';
  let n = 2;
  let candidate = `${base}_${n}${ext}`;
  while (used.has(candidate)) {
    n += 1;
    candidate = `${base}_${n}${ext}`;
  }
  used.add(candidate);
  return candidate;
}

async function fetchAttachmentBlob(attachment, request) {
  if (attachment.key && request) {
    const res = await request(`/api/health/files/content?key=${encodeURIComponent(attachment.key)}`);
    if (res.ok) return res.blob();
  }
  if (attachment.url) {
    const res = await fetch(attachment.url);
    if (res.ok) return res.blob();
  }
  throw new Error('fetch_failed');
}

export async function downloadHealthFilesZip(files, request) {
  const zip = new JSZip();
  const usedNames = new Set();
  let added = 0;

  for (let f = 0; f < files.length; f += 1) {
    const file = files[f];
    const toDownload = attachmentsForDownload(file);
    for (let i = 0; i < toDownload.length; i += 1) {
      const attachment = toDownload[i];
      const filename = ensureUnique(
        downloadFilenameForAttachment(file, attachment, i, toDownload.length),
        usedNames
      );
      const blob = await fetchAttachmentBlob(attachment, request);
      const buf = await blob.arrayBuffer();
      zip.file(filename, buf);
      added += 1;
    }
  }

  if (added === 0) {
    throw new Error('nothing_to_download');
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `health-files-${istTimestampForFilename(new Date().toISOString())}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
